import { MapPin, Radio, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, Stagger, staggerItem } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";
import Cta from "@/components/Cta";
import { motion } from "framer-motion";
import { SITE, IMAGES } from "@/lib/site";

export default function Coverage() {
  return (
    <>
      <Seo
        title="Coverage Area — Rural Internet in Cortez, Dolores & Yellow Jacket | CYAN Wireless"
        description="Check CYAN Wireless internet coverage across Cortez, Dolores, Yellow Jacket and Lewis, Colorado. Request a free site survey to confirm availability at your address."
        path="/coverage"
      />
      <PageHero
        overline="Service Area"
        titleLines={["Are we in", <span key="2">your <span className="text-cyan-400 text-glow">area?</span></span>]}
        subtitle={`CYAN Wireless is expanding across rural ${SITE.state}. We proudly serve ${SITE.areas.join(", ")} — and we're always growing. Not sure if you're covered? A free site survey answers it fast.`}
        image={IMAGES.towerMountain}
        imageAlt="Wireless tower against Colorado mountains"
      >
        <Cta to="/survey" data-testid="coverage-survey-cta">Request Free Site Survey <ArrowRight size={16} /></Cta>
      </PageHero>

      <section className="py-20 sm:py-28 bg-ink-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="mb-12">
            <p className="overline mb-4">Where we serve</p>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tighter text-white">Covered communities</h2>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SITE.areas.map((a) => (
              <motion.div key={a} variants={staggerItem} className="group bg-ink-900 border border-white/5 rounded-2xl p-7 hover:border-cyan-500/50 transition-colors">
                <MapPin className="text-cyan-400 mb-4" size={22} />
                <h3 className="font-display font-bold text-xl text-white">{a}</h3>
                <p className="text-zinc-500 text-sm mt-1">{SITE.state}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="pb-24 bg-ink-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-stretch">
          <Reveal className="relative rounded-2xl overflow-hidden border border-white/10 min-h-[360px]">
            <iframe
              title="CYAN Wireless coverage map"
              className="absolute inset-0 w-full h-full grayscale contrast-125 opacity-80"
              src="https://www.google.com/maps?q=Cortez,Colorado&z=10&output=embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="absolute inset-0 pointer-events-none bg-cyan-500/10 mix-blend-overlay" />
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-ink-950/80 backdrop-blur px-3 py-1.5 rounded-full border border-white/10">
              <Radio size={14} className="text-cyan-400 animate-pulse-glow" />
              <span className="font-mono-tech text-[11px] uppercase tracking-widest text-white">Live service region</span>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="bg-ink-900 border border-white/10 rounded-2xl p-8">
            <h3 className="font-display font-bold text-2xl text-white mb-2">Check your address</h3>
            <p className="text-zinc-400 text-sm mb-6">Drop your details and we'll confirm availability and schedule a free site survey.</p>
            <LeadForm type="site_survey" submitLabel="Check Availability" showAddress compact />
          </Reveal>
        </div>
      </section>
    </>
  );
}
